import { useEffect, useMemo, useState } from 'react';
import { GAME, fixOpensAt, fixPuzzleFor, solvesNeeded } from '@continuum/show-core';
import type { FixPuzzle, GuestIntent } from '@continuum/show-core';
import type { WireAnomaly, WireState, You } from '../room/client.js';
import { BottomSheet } from '../ui/BottomSheet.js';
import { Hero, PrimaryButton, mono, sans } from '../ui/atoms.js';
import { FIX_COPY } from '../ui/fixText.js';
import { GAME_COPY } from '../ui/gameText.js';
import type { Lang } from '../ui/text.js';
import { FixPuzzleBoard } from '../games/FixPuzzle.js';

/**
 * FIX, on the phone of a guest the room handed it to (Marek, 2026-09-29): the whole screen, first a
 * countdown — FIX MISSION IN 10 … 1, who is on it, a buzz as it starts — then their own puzzle
 * (games/FixPuzzle.tsx) with the alarm's clock and how many have fixed it, never who. Solved, it
 * says so and waits for the others. Pulled down, it shrinks to the alarm bar at the top, which
 * brings it back. The countdown's number changes once a second; nothing flashes.
 */

const AGENT = '#ff5f86';

function clock(ms: number): string {
  const s = Math.max(0, Math.ceil(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

export function FixMission({
  lang,
  state,
  anomaly,
  you,
  now,
  others,
  send,
  onMinimize,
}: {
  lang: Lang;
  state: WireState;
  anomaly: WireAnomaly;
  you: You;
  now: number;
  /** Call signs of the others handed FIX. */
  others: string[];
  send: (intent: GuestIntent) => void;
  onMinimize: () => void;
}) {
  const f = FIX_COPY[lang];
  const g = GAME_COPY[lang];
  const opensAt = fixOpensAt(anomaly);
  const counting = now < opensAt;
  const puzzle = useMemo(() => fixPuzzleFor(state.voyageId, anomaly.id, you.seat), [state.voyageId, anomaly.id, you.seat]);
  const [solvedHere, setSolvedHere] = useState(false);
  const solved = solvedHere || (you.solved ?? []).includes(anomaly.id);
  const need = solvesNeeded(anomaly);
  const fixes = Math.min(need, Math.max(anomaly.fixes ?? 0, solvedHere ? 1 : 0));
  // At a stop's end the alarms go off one after another (show-core stop.ts): which one this is.
  const stop = state.game.stop && state.game.stop.chapter === state.chapter ? state.game.stop : null;
  const alarmOf = stop && stop.alarms > 1 && stop.step === 'alarms' ? ` ${g.alarmOf(Math.max(1, stop.fired), stop.alarms)}` : '';

  // A buzz as the mission starts, where the phone allows it (iPhones do not).
  useEffect(() => {
    navigator.vibrate?.([200, 100, 200]);
  }, [anomaly.id]);

  return (
    <BottomSheet
      id={`fix:${anomaly.id}`}
      size="full"
      critical
      zIndex={63}
      label={f.missionIn(Math.ceil((opensAt - now) / 1000))}
      onDismiss={onMinimize}
      closeLabel={`▾ ${f.hide}`}
      tone={{ bg: '#1a0409', fg: '#ffe0e6', border: AGENT, grip: AGENT }}
      header={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
          <span style={{ fontFamily: sans, fontSize: 20, fontWeight: 700, letterSpacing: 2, color: AGENT }}>
            ⚠ {g.anomaly}
            {alarmOf} · {g.near(anomaly.near[0], anomaly.near[1])}
          </span>
          {!counting && <span style={{ fontFamily: mono, fontSize: 18, color: '#ffd1da' }}>{f.left(clock(anomaly.deadline - now))}</span>}
        </div>
      }
    >
      {counting ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingTop: 12 }}>
          {/* One big line, its number going down once a second. */}
          {/* Sized to the phone's width: FIKSEOPPDRAG is one long word on a small phone. */}
          <Hero color="#ffffff" style={{ fontSize: 'clamp(38px, 12.4vw, 56px)', fontWeight: 800, lineHeight: 1, letterSpacing: 1 }}>
            {f.missionIn(Math.max(1, Math.ceil((opensAt - now) / 1000)))}
          </Hero>
          <Hero>{f.missionWho(others)}</Hero>
          <div style={{ fontFamily: sans, fontSize: 19, fontWeight: 600, lineHeight: 1.3, color: '#ffd1da' }}>{f.getReady}</div>
        </div>
      ) : you.brig ? (
        <Hero style={{ paddingTop: 16 }}>{f.votedOut}</Hero>
      ) : solved ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, paddingTop: 16 }}>
          <Hero color="#4fd8c2" style={{ fontSize: 48, fontWeight: 800 }}>
            {f.solved}
          </Hero>
          <Hero>{f.solvedWait}</Hero>
          <div style={{ fontFamily: mono, fontSize: 16, color: '#ffd1da' }}>{f.count(fixes, need)}</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, paddingTop: 4 }}>
          <div style={{ fontFamily: mono, fontSize: 14, color: '#ffd1da' }}>{f.count(fixes, need)}</div>
          <FixPuzzleBoard
            kind={puzzle.kind}
            seed={puzzle.seed}
            lang={lang}
            onSolved={() => {
              setSolvedHere(true);
              navigator.vibrate?.(40);
              send({ type: 'fixSolved', anomaly: anomaly.id });
            }}
          />
        </div>
      )}
    </BottomSheet>
  );
}

function useNow(ms: number): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(id);
  }, [ms]);
  return now;
}

/**
 * One FIX puzzle on the practice page (practice.tsx), as a called guest meets it: the ten-second
 * countdown, then the puzzle against the forty seconds. Solved calls `onSolved`; out of time, NEXT.
 * Nothing is sent anywhere.
 */
export function FixPractice({
  lang,
  kind,
  seed,
  onSolved,
  onNext,
}: {
  lang: Lang;
  kind: FixPuzzle;
  seed: number;
  onSolved: () => void;
  onNext: () => void;
}) {
  const f = FIX_COPY[lang];
  const [start] = useState(() => Date.now());
  const [solved, setSolved] = useState(false);
  const now = useNow(250);
  const opensAt = start + GAME.FIX_COUNTDOWN_MS;
  const deadline = opensAt + GAME.STABILISE_WINDOW_MS;
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 12, padding: '16px 20px 20px', boxSizing: 'border-box', overflowY: 'auto', color: '#ffe0e6' }}>
      {now < opensAt ? (
        <>
          <Hero color="#ffffff" style={{ fontSize: 56, fontWeight: 800, lineHeight: 1, letterSpacing: 1 }}>
            {f.missionIn(Math.max(1, Math.ceil((opensAt - now) / 1000)))}
          </Hero>
          <Hero>{f.missionWho([])}</Hero>
          <div style={{ fontFamily: sans, fontSize: 19, fontWeight: 600, lineHeight: 1.3, color: '#ffd1da' }}>{f.getReady}</div>
        </>
      ) : now >= deadline && !solved ? (
        <>
          <Hero color={AGENT} style={{ fontSize: 40, fontWeight: 800 }}>
            {f.timeUp}
          </Hero>
          <PrimaryButton big bg={AGENT} fg="#2a0610" onClick={onNext}>
            {f.tryNext}
          </PrimaryButton>
        </>
      ) : (
        <>
          <div style={{ fontFamily: mono, fontSize: 18, color: '#ffd1da' }}>{f.left(clock(deadline - now))}</div>
          <FixPuzzleBoard
            kind={kind}
            seed={seed}
            lang={lang}
            onSolved={() => {
              setSolved(true);
              onSolved();
            }}
          />
        </>
      )}
    </div>
  );
}
